import { Injectable } from '@angular/core';
import { Store, select } from '@ngrx/store';
import { AuthService, StateUtils } from '@spartacus/core';
import { Observable } from 'rxjs';
import { filter, map, share, shareReplay, switchMap, tap, withLatestFrom } from 'rxjs/operators';
import { UserIdService } from '../../../core/auth';
import { SearchAction } from '../../../core/features/store/actions/search.actions';
import { AddressSearchParams, DeliveryAddress, Pagination } from '../../../core/model';
import { AvailableSoldTosSearchParams, SoldTo } from '../../../core/model/user.model';
import { withdrawOn } from '../../../core/util';
import { SoldToFacade } from '../facades';
import { SoldToActions } from '../store/actions';
import { SoldToSelectors } from '../store/selectors/index';
import { DELIVERY_ADDRESS, SOLD_TOS, StateWithSoldTo } from '../store/sold-to-state';

@Injectable()
export class SoldToService implements SoldToFacade {
  constructor(private store: Store<StateWithSoldTo>, private authService: AuthService, private userIdService: UserIdService) {}

  searchForAvailableSoldTos(searchParams: AvailableSoldTosSearchParams) {
    this.userIdService
      .takeUserId(true)
      .pipe(withLatestFrom(this.needsToLoadAvailableSoldTos()))
      .subscribe(
        ([userId, needsToLoad]) => {
          if (needsToLoad) {
            this.store.dispatch(new SoldToActions.LoadAvailableSoldTos(searchParams, userId));
          }
        },
        () => {}
      );
  }

  getSearchParametersForAvailableSoldTos(): Observable<AvailableSoldTosSearchParams> {
    return this.store.select(SoldToSelectors.getAvailableSearchParamsSelector).pipe(
      map((params) => params as AvailableSoldTosSearchParams),
      tap((params: AvailableSoldTosSearchParams) => {
        if (!params || !params.key) {
          this.updateSearchParametersForAvailableSoldTos({
            key: 'latest',
            page: 0,
            count: 25,
          });
        }
      }),
      filter((params) => params && !!params.key)
    );
  }

  updateSearchParametersForAvailableSoldTos(searchParams: AvailableSoldTosSearchParams) {
    this.store.dispatch(new SearchAction(searchParams, SOLD_TOS));
  }

  getAvailableSoldTosPagination(): Observable<Pagination | undefined> {
    return this.store.select(SoldToSelectors.getAvailableSearchPaginationSelector);
  }

  private needsToLoadAvailableSoldTos(): Observable<boolean> {
    return this.store
      .select(SoldToSelectors.getAvailableSearchPageResultsSelector)
      .pipe(map((loaderState) => !loaderState.loading && !loaderState.success && !loaderState.error));
  }

  getAvailableSoldTosLoading(): Observable<boolean> {
    return this.store
      .select(SoldToSelectors.getAvailableSearchPageResultsSelector)
      .pipe(map((loaderState) => loaderState.loading));
  }

  getAvailableSoldTos(): Observable<Array<SoldTo>> {
    return this.store.select(SoldToSelectors.getAvailableSearchPageResultsSelector).pipe(
      filter((loaderState) => loaderState.success),
      map((loaderState) => loaderState.value.results),
      switchMap((results) => this.store.select(SoldToSelectors.getAvailableSearchResultEntries, { keys: results }))
    );
  }

  getAvailableSearchResultEntitiesCount(): Observable<number> {
    return this.store
      .select(SoldToSelectors.getAvailableSearchResultEntities)
      .pipe(map((entities) => Object.keys(entities)?.length || 0));
  }

  private loadActiveSoldTo(oldSoldToId?: string): void {
    this.userIdService.takeUserId(true).subscribe(
      (userId) => this.store.dispatch(new SoldToActions.LoadActiveSoldTo(userId, oldSoldToId)),
      () => {}
    );
  }

  getActiveSoldTo(forceLoad?: boolean): Observable<SoldTo | undefined> {
    const isUserLoggedIn$ = this.authService.isUserLoggedIn().pipe(share());

    return isUserLoggedIn$.pipe(
      filter(Boolean),
      switchMap(() => this.getActiveSoldToState()),
      filter((loaderState) => {
        if ((!loaderState.success && !loaderState.error && !loaderState.loading) || (forceLoad && !loaderState.loading)) {
          this.loadActiveSoldTo(forceLoad ? loaderState?.value?.uid : undefined);
          return false;
        }
        return loaderState.success;
      }),
      map((loaderState) => loaderState.value),
      withdrawOn(isUserLoggedIn$.pipe(filter((isLoggedIn) => !isLoggedIn))),
      shareReplay({ bufferSize: 1, refCount: true })
    );
  }

  private getActiveSoldToState(): Observable<StateUtils.LoaderState<SoldTo>> {
    return this.store.select(SoldToSelectors.getActiveSoldToLoaderState);
  }

  getActiveSoldToLoading(): Observable<boolean> {
    return this.store.pipe(select(SoldToSelectors.getActiveSoldToLoading));
  }

  getActiveSoldToLoaded(): Observable<boolean> {
    return this.store.pipe(select(SoldToSelectors.getActiveSoldToLoaded));
  }

  clearActiveSoldTo(): void {
    this.store.dispatch(new SoldToActions.ClearActiveSoldTo());
  }

  changeSoldTo(soldTo: string) {
    this.userIdService.takeUserId(true).subscribe(
      (userId) => this.store.dispatch(new SoldToActions.ChangeSoldTo({ userId, soldTo })),
      () => {}
    );
  }

  searchForDeliveryAddresses(searchParams: AddressSearchParams) {
    this.userIdService.takeUserId(true).subscribe(
      (userId) => this.store.dispatch(new SoldToActions.LoadDeliveryAddresses(searchParams, userId)),
      () => {}
    );
  }

  getSearchParametersForDeliveryAddresses(): Observable<AddressSearchParams> {
    return this.store.select(SoldToSelectors.getSearchParamsSelector).pipe(
      map((params) => params as AddressSearchParams),
      tap((params: AddressSearchParams) => {
        if (!params || !params.key) {
          this.updateSearchParametersForDeliveryAddresses({
            page: 0,
            count: 10,
          });
        }
      }),
      filter((params) => params && !!params.key)
    );
  }

  updateSearchParametersForDeliveryAddresses(searchParams: Omit<AddressSearchParams, 'key'>) {
    const key = searchParams.text ? `t=${searchParams.text}` : 'latest';
    this.store.dispatch(new SearchAction({ key, ...searchParams }, DELIVERY_ADDRESS));
  }

  getDeliveryAddressPagination(): Observable<Pagination | undefined> {
    return this.store.select(SoldToSelectors.getSearchPaginationSelector);
  }

  getDeliveryAddressesLoading(): Observable<boolean> {
    return this.store.select(SoldToSelectors.getSearchPageResultsSelector).pipe(map((loaderState) => loaderState.loading));
  }

  getDeliveryAddresses(): Observable<Array<DeliveryAddress>> {
    return this.store.select(SoldToSelectors.getSearchPageResultsSelector).pipe(
      filter((loaderState) => loaderState.success),
      map((loaderState) => loaderState.value.results),
      switchMap((results) => this.store.select(SoldToSelectors.getSearchResultEntries, { keys: results }))
    );
  }

  isCreditBlocked(): Observable<boolean> {
    return this.getActiveSoldTo().pipe(
      map((soldTo) => {
        return soldTo?.creditBlocked || false;
      })
    );
  }
}
